import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError, shortDate, num } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Users, Clock, CalendarBlank, Trophy, Money, LockOpen } from "@phosphor-icons/react";

const MONTH_NAMES = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function hFmt(n) {
    return `${(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`;
}

function startOfWeekISO(d) {
    const dt = new Date(d);
    const day = dt.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday start
    dt.setDate(dt.getDate() + diff);
    dt.setHours(0, 0, 0, 0);
    // Usa la data locale (non UTC) per evitare off-by-one in fusi con offset
    // positivo: toISOString() avrebbe spostato la mezzanotte locale del lunedì
    // alla sera della domenica UTC, facendo partire il calendario dalla domenica.
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day2 = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day2}`;
}

function parseISODateLocal(s) {
    // Parsa "YYYY-MM-DD" come data locale (non UTC) per evitare lo stesso bug
    // al re-render del calendario.
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
}

function timeAgo(iso) {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    const hh = Math.floor(ms / 3_600_000);
    if (hh < 1) return "ora";
    if (hh < 24) return `${hh}h fa`;
    const days = Math.floor(hh / 24);
    return `${days}g fa`;
}

export default function Team() {
    const { user, agencyId } = useAuth();
    const [tab, setTab] = useState("performance");
    const [showOverride, setShowOverride] = useState(false);

    if (!agencyId) {
        return <div className="overline p-6">Seleziona un&apos;agenzia.</div>;
    }
    if (!["owner", "agency_manager"].includes(user?.role)) {
        return (
            <div className="max-w-xl py-16">
                <div className="overline mb-2">403</div>
                <h1 className="text-3xl font-black tracking-tighter">Non autorizzato</h1>
                <p className="text-sm text-zinc-600 mt-2">
                    La gestione del team è riservata a owner e agency manager.
                </p>
            </div>
        );
    }

    return (
        <div data-testid="team-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="overline mb-2">Gestione</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Team</h1>
                </div>
                <button
                    onClick={() => setShowOverride(true)}
                    className="btn-secondary inline-flex items-center gap-2 self-start"
                    data-testid="open-shift-override-btn"
                    title="Apri il turno a un operatore bloccato (es. codice di sede dimenticato o scaduto)"
                >
                    <LockOpen size={14} weight="bold" /> Apri turno per un operatore
                </button>
            </div>

            <div className="flex border-b border-zinc-200 mb-6 overflow-x-auto">
                <TabButton active={tab === "performance"} onClick={() => setTab("performance")} testid="tab-performance">
                    <Trophy size={14} weight="bold" /> Performance
                </TabButton>
                <TabButton active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">
                    <Clock size={14} weight="bold" /> Storico turni
                </TabButton>
                <TabButton active={tab === "schedule"} onClick={() => setTab("schedule")} testid="tab-schedule">
                    <CalendarBlank size={14} weight="bold" /> Schedulazione
                </TabButton>
                <TabButton active={tab === "payroll"} onClick={() => setTab("payroll")} testid="tab-payroll">
                    <Money size={14} weight="bold" /> Buste paga
                </TabButton>
            </div>

            {tab === "performance" && <PerformanceTab agencyId={agencyId} />}
            {tab === "history" && <HistoryTab agencyId={agencyId} />}
            {tab === "schedule" && <ScheduleTab agencyId={agencyId} />}
            {tab === "payroll" && <PayrollTab agencyId={agencyId} />}

            {showOverride && (
                <OverrideShiftModal agencyId={agencyId} onClose={() => setShowOverride(false)} />
            )}
        </div>
    );
}


function OverrideShiftModal({ agencyId, onClose }) {
    const [operators, setOperators] = useState([]);
    const [operatorId, setOperatorId] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/users").then((r) => {
            setOperators(
                (r.data || []).filter((u) => {
                    const ids = new Set(u.agency_ids || []);
                    if (u.agency_id) ids.add(u.agency_id);
                    return ids.has(agencyId) && u.role === "operator";
                }),
            );
        });
    }, [agencyId]);

    const submit = async () => {
        if (!operatorId) {
            toast.error("Seleziona un operatore");
            return;
        }
        setSaving(true);
        try {
            await api.post("/shifts/open-for-operator", { agency_id: agencyId, operator_id: operatorId, notes });
            toast.success("Turno aperto per l'operatore selezionato");
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-white/80 backdrop-blur-sm z-[60] overflow-y-auto"
            onClick={onClose}
            data-testid="shift-override-modal"
        >
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-md bg-white border border-zinc-200 p-6 md:p-8 shadow-2xl"
                >
                    <div className="overline mb-3 flex items-center gap-2">
                        <LockOpen size={12} weight="bold" /> Escape hatch
                    </div>
                    <h2 className="text-3xl font-black tracking-tighter mb-3">
                        Apri turno per un operatore
                    </h2>
                    <p className="text-sm text-zinc-600 mb-6">
                        Usalo quando un operatore realmente in sede non riesce ad aprire il turno
                        (es. schermo della sede spento, codice dimenticato o scaduto). Il turno
                        viene aperto saltando il controllo del codice e l'azione resta sempre
                        tracciata in audit log.
                    </p>
                    <label className="block mb-4">
                        <div className="overline mb-2">Operatore</div>
                        <select
                            className="inp"
                            value={operatorId}
                            onChange={(e) => setOperatorId(e.target.value)}
                            data-testid="shift-override-operator-select"
                        >
                            <option value="">Seleziona…</option>
                            {operators.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="block mb-6">
                        <div className="overline mb-2">Motivo (opzionale)</div>
                        <input
                            className="inp"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Es. schermo Vista sede spento, operatore confermato in sede al telefono"
                            data-testid="shift-override-notes"
                        />
                    </label>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="btn-secondary flex-1" data-testid="shift-override-cancel">
                            Annulla
                        </button>
                        <button
                            onClick={submit}
                            disabled={saving}
                            className="btn-primary flex-1"
                            data-testid="shift-override-confirm"
                        >
                            {saving ? "Apertura…" : "Apri turno"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


function TabButton({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-5 py-3 text-sm font-bold inline-flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                active
                    ? "border-signal text-ink"
                    : "border-transparent text-zinc-500 hover:text-ink"
            }`}
        >
            {children}
        </button>
    );
}


function PerformanceTab({ agencyId }) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/team/performance?agency_id=${agencyId}&year=${year}&month=${month}`)
            .then((r) => setRows(r.data.rows || []))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, [agencyId, year, month]);

    const monthOpts = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
        for (let m = 12; m >= 1; m--) {
            if (y === now.getFullYear() && m > now.getMonth() + 1) continue;
            monthOpts.push({ y, m, label: `${MONTH_NAMES[m - 1]} ${y}` });
        }
    }

    const totalShortage = rows.reduce((s, r) => s + r.total_shortage, 0);
    const totalOverage = rows.reduce((s, r) => s + r.total_overage, 0);
    const totalShifts = rows.reduce((s, r) => s + r.shifts_count, 0);

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                <label className="block">
                    <div className="overline mb-1">Periodo</div>
                    <select
                        className="inp"
                        value={`${year}-${month}`}
                        onChange={(e) => {
                            const [y, m] = e.target.value.split("-");
                            setYear(parseInt(y));
                            setMonth(parseInt(m));
                        }}
                        data-testid="perf-period-select"
                    >
                        {monthOpts.map((o) => (
                            <option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <KpiCard label="Turni totali" value={totalShifts} />
                <KpiCard label="Ammanchi cumulati" value={euro(totalShortage)} color="red" />
                <KpiCard label="Eccedenze cumulate" value={euro(totalOverage)} color="emerald" />
            </div>
            <div className="text-xs text-zinc-500 mb-6 max-w-2xl">
                La colonna "Ritardi" mostra anche una <strong>stima informativa</strong> del costo (minuti di
                ritardo × tariffa oraria): serve solo a valutare eventuali provvedimenti verso l'operatore.
                Non viene mai sottratta automaticamente dal totale in Buste paga — resta sempre una tua scelta
                come, quando e se intervenire sul pagamento.
            </div>

            {loading ? (
                <div className="overline">Caricamento…</div>
            ) : rows.length === 0 ? (
                <div className="p-8 border border-zinc-200 bg-white text-center">
                    <Users size={28} weight="bold" className="mx-auto text-zinc-300 mb-2" />
                    <div className="text-sm font-bold">Nessun turno chiuso nel periodo</div>
                    <div className="text-xs text-zinc-500 mt-1">
                        Le statistiche operatori compariranno qui dopo le prime chiusure turno.
                    </div>
                </div>
            ) : (
                <div className="bg-white border border-zinc-200 overflow-x-auto">
                    <table className="ft-table" data-testid="perf-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Operatore</th>
                                <th className="text-right">Turni</th>
                                <th className="text-right">Ammanchi</th>
                                <th className="text-right">Eccedenze</th>
                                <th className="text-right">Netto</th>
                                <th className="text-right">Ritardi</th>
                                <th>Ultimo turno</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={r.operator_id} data-testid={`perf-row-${r.operator_id}`}>
                                    <td className="font-bold tabular-nums">{idx + 1}</td>
                                    <td>
                                        <div className="font-bold">{r.operator_name}</div>
                                        <div className="overline">{r.operator_role}</div>
                                    </td>
                                    <td className="text-right font-bold tabular-nums">{r.shifts_count}</td>
                                    <td className="text-right tabular-nums">
                                        {r.total_shortage > 0 ? (
                                            <span className="text-red-700 font-bold">−{euro(r.total_shortage)}</span>
                                        ) : (
                                            <span className="text-zinc-400">0,00 €</span>
                                        )}
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {r.total_overage > 0 ? (
                                            <span className="text-emerald-700 font-bold">+{euro(r.total_overage)}</span>
                                        ) : (
                                            <span className="text-zinc-400">0,00 €</span>
                                        )}
                                    </td>
                                    <td className="text-right tabular-nums font-bold">
                                        <span className={r.net_discrepancy < 0 ? "text-red-700" : r.net_discrepancy > 0 ? "text-emerald-700" : "text-zinc-500"}>
                                            {r.net_discrepancy >= 0 ? "+" : ""}
                                            {euro(r.net_discrepancy)}
                                        </span>
                                    </td>
                                    <td className="text-right tabular-nums" data-testid={`perf-late-${r.operator_id}`}>
                                        {r.late_count > 0 ? (
                                            <div>
                                                <div className="text-amber-700 font-bold">
                                                    {r.late_count}× ({r.late_minutes_total} min)
                                                </div>
                                                {r.late_cost_estimate > 0 && (
                                                    <div
                                                        className="text-[11px] text-zinc-400"
                                                        title="Stima informativa: minuti di ritardo × tariffa oraria. Non viene mai sottratta automaticamente dalla busta paga."
                                                        data-testid={`perf-late-cost-${r.operator_id}`}
                                                    >
                                                        ~{euro(r.late_cost_estimate)} stimati
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-zinc-400">—</span>
                                        )}
                                    </td>
                                    <td className="text-xs text-zinc-500">{timeAgo(r.last_shift_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function KpiCard({ label, value, color = "zinc" }) {
    const colorClass = {
        red: "text-red-700",
        emerald: "text-emerald-700",
        zinc: "text-ink",
    }[color];
    return (
        <div className="p-4 bg-white border border-zinc-200">
            <div className="overline mb-1">{label}</div>
            <div className={`text-2xl font-black tracking-tighter tabular-nums ${colorClass}`}>{value}</div>
        </div>
    );
}

function PayrollTab({ agencyId }) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ total_hours: 0, total_due: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/team/payroll?agency_id=${agencyId}&year=${year}&month=${month}`)
            .then((r) => {
                setRows(r.data.rows || []);
                setTotals({ total_hours: r.data.total_hours || 0, total_due: r.data.total_due || 0 });
            })
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, [agencyId, year, month]);

    const monthOpts = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
        for (let m = 12; m >= 1; m--) {
            monthOpts.push({ y, m, label: `${MONTH_NAMES[m - 1]} ${y}` });
        }
    }

    return (
        <div data-testid="payroll-tab">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                <label className="block">
                    <div className="overline mb-1">Periodo</div>
                    <select
                        className="inp"
                        value={`${year}-${month}`}
                        onChange={(e) => {
                            const [y, m] = e.target.value.split("-");
                            setYear(parseInt(y));
                            setMonth(parseInt(m));
                        }}
                        data-testid="payroll-period-select"
                    >
                        {monthOpts.map((o) => (
                            <option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="text-xs text-zinc-500 mb-6 max-w-2xl">
                Calcolo basato sui turni <strong>fissati a calendario</strong> (Schedulazione) nel mese
                selezionato, moltiplicati per la tariffa oraria impostata su ciascun utente in{" "}
                <strong>Impostazioni → Utenti</strong>. Non riflette i turni di cassa effettivamente aperti/chiusi.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                <KpiCard label="Ore pianificate totali" value={hFmt(totals.total_hours)} />
                <KpiCard label="Totale da pagare" value={euro(totals.total_due)} color="emerald" />
            </div>

            {loading ? (
                <div className="overline">Caricamento…</div>
            ) : rows.length === 0 ? (
                <div className="p-8 border border-zinc-200 bg-white text-center">
                    <Money size={28} weight="bold" className="mx-auto text-zinc-300 mb-2" />
                    <div className="text-sm font-bold">Nessun turno pianificato nel periodo</div>
                    <div className="text-xs text-zinc-500 mt-1">
                        Aggiungi turni in Schedulazione e tariffe orarie in Impostazioni → Utenti.
                    </div>
                </div>
            ) : (
                <div className="bg-white border border-zinc-200 overflow-x-auto">
                    <table className="ft-table" data-testid="payroll-table">
                        <thead>
                            <tr>
                                <th>Operatore</th>
                                <th className="text-right">Ore pianificate</th>
                                <th className="text-right">Tariffa oraria</th>
                                <th className="text-right">Totale dovuto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.operator_id} data-testid={`payroll-row-${r.operator_id}`}>
                                    <td>
                                        <div className="font-bold">{r.operator_name}</div>
                                        <div className="overline">{r.operator_role}</div>
                                    </td>
                                    <td className="text-right font-bold tabular-nums">{hFmt(r.scheduled_hours)}</td>
                                    <td className="text-right tabular-nums">
                                        {r.hourly_rate_set ? (
                                            `${num(r.hourly_rate)} €/h`
                                        ) : (
                                            <span className="text-amber-700 text-xs font-bold">non impostata</span>
                                        )}
                                    </td>
                                    <td className="text-right tabular-nums font-black">{euro(r.total_due)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}



function HistoryTab({ agencyId }) {
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        api.get(`/team/shifts?agency_id=${agencyId}&limit=50`)
            .then((r) => setShifts(r.data || []))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, [agencyId]);

    if (loading) return <div className="overline">Caricamento…</div>;
    if (shifts.length === 0) {
        return (
            <div className="p-8 border border-zinc-200 bg-white text-center">
                <Clock size={28} weight="bold" className="mx-auto text-zinc-300 mb-2" />
                <div className="text-sm font-bold">Nessun turno registrato</div>
            </div>
        );
    }
    return (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="ft-table" data-testid="history-table">
                <thead>
                    <tr>
                        <th>Operatore</th>
                        <th>Apertura</th>
                        <th>Chiusura</th>
                        <th className="text-right">Cassa attesa</th>
                        <th className="text-right">Contata</th>
                        <th className="text-right">Scarto</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {shifts.map((s) => {
                        const isOpen = s.status === "open";
                        const isExpanded = expanded === s.id;
                        return (
                            <React.Fragment key={s.id}>
                                <tr
                                    onClick={() => setExpanded(isExpanded ? null : s.id)}
                                    className="cursor-pointer hover:bg-zinc-50"
                                    data-testid={`history-row-${s.id}`}
                                >
                                    <td>
                                        <div className="font-bold">{s.operator_name}</div>
                                        <div className="overline">{s.operator_role}</div>
                                    </td>
                                    <td className="text-xs">{shortDate(s.opened_at)}</td>
                                    <td className="text-xs">{s.closed_at ? shortDate(s.closed_at) : "—"}</td>
                                    <td className="text-right tabular-nums">{s.cash_expected != null ? euro(s.cash_expected) : "—"}</td>
                                    <td className="text-right tabular-nums">{s.cash_counted != null ? euro(s.cash_counted) : "—"}</td>
                                    <td className="text-right tabular-nums font-bold">
                                        {s.cash_discrepancy == null ? (
                                            "—"
                                        ) : (
                                            <span className={s.cash_discrepancy < 0 ? "text-red-700" : s.cash_discrepancy > 0 ? "text-emerald-700" : "text-zinc-500"}>
                                                {s.cash_discrepancy >= 0 ? "+" : ""}
                                                {euro(s.cash_discrepancy)}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`overline ${isOpen ? "text-emerald-700" : "text-zinc-500"}`}>
                                            {isOpen ? "Aperto" : "Chiuso"}
                                        </span>
                                    </td>
                                </tr>
                                {isExpanded && (
                                    <tr className="bg-zinc-50">
                                        <td colSpan={7} className="px-6 py-4 text-xs">
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div>
                                                    <div className="overline">Baseline cassa</div>
                                                    <div className="font-bold tabular-nums">{euro(s.cash_baseline || 0)}</div>
                                                </div>
                                                <div>
                                                    <div className="overline">POS chiusi</div>
                                                    <div className="font-bold">{(s.pos_closures || []).length}</div>
                                                </div>
                                                <div>
                                                    <div className="overline">Commissioni POS</div>
                                                    <div className="font-bold tabular-nums">{euro(s.pos_fees_total || 0)}</div>
                                                </div>
                                                <div>
                                                    <div className="overline">Durata</div>
                                                    <div className="font-bold">
                                                        {s.closed_at
                                                            ? `${Math.floor((new Date(s.closed_at) - new Date(s.opened_at)) / 3_600_000)}h ${Math.floor(((new Date(s.closed_at) - new Date(s.opened_at)) % 3_600_000) / 60_000)}m`
                                                            : "in corso"}
                                                    </div>
                                                </div>
                                            </div>
                                            {s.notes_close && (
                                                <div className="mt-3 text-zinc-600">
                                                    <strong>Note chiusura:</strong> {s.notes_close}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


// Hash deterministico nome → indice palette, usato per colorare i blocchi turno.
const OPERATOR_PALETTE = [
    { bg: "#FEE2E2", fg: "#991B1B", bar: "#DC2626" }, // rosso
    { bg: "#DBEAFE", fg: "#1E3A8A", bar: "#2563EB" }, // blu
    { bg: "#D1FAE5", fg: "#065F46", bar: "#10B981" }, // verde
    { bg: "#FEF3C7", fg: "#92400E", bar: "#F59E0B" }, // ambra
    { bg: "#EDE9FE", fg: "#5B21B6", bar: "#8B5CF6" }, // viola
    { bg: "#FCE7F3", fg: "#9D174D", bar: "#EC4899" }, // rosa
    { bg: "#CCFBF1", fg: "#115E59", bar: "#14B8A6" }, // teal
    { bg: "#E0E7FF", fg: "#3730A3", bar: "#6366F1" }, // indaco
];

function operatorColor(name) {
    if (!name) return OPERATOR_PALETTE[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) {
        h = (h * 31 + name.charCodeAt(i)) >>> 0;
    }
    return OPERATOR_PALETTE[h % OPERATOR_PALETTE.length];
}

function diffHours(start, end) {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60; // turno notturno
    return mins / 60;
}

function ScheduleTab({ agencyId }) {
    const [viewMode, setViewMode] = useState("month"); // "month" | "week"
    const [cursor, setCursor] = useState(() => {
        // Riferimento centrale: mese visualizzato (vista mese) o lunedì (vista settimana)
        const t = new Date();
        return new Date(t.getFullYear(), t.getMonth(), 1);
    });
    const [slots, setSlots] = useState([]);
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editor, setEditor] = useState(null); // {weekday, weekStarting, slot|null, date}
    const [duplicating, setDuplicating] = useState(false);

    // Calcola il range di settimane da caricare (5-6 settimane per la vista mese,
    // 1 settimana per la vista settimana). Tutto in tempo locale per evitare
    // off-by-one con i fusi GMT+1/+2.
    const { fromWeek, toWeek, weekStartingForCurrent } = useMemo(() => {
        if (viewMode === "week") {
            const ws = startOfWeekISO(cursor);
            return { fromWeek: ws, toWeek: ws, weekStartingForCurrent: ws };
        }
        // vista mese: dal lunedì che contiene il giorno 1 al lunedì che contiene il giorno 28+
        const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const lastOfMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
        const start = parseISODateLocal(startOfWeekISO(firstOfMonth));
        // Garantiamo almeno 6 righe → 6 settimane
        const end = parseISODateLocal(startOfWeekISO(lastOfMonth));
        return {
            fromWeek: startOfWeekISO(start),
            toWeek: startOfWeekISO(end),
            weekStartingForCurrent: startOfWeekISO(new Date()),
        };
    }, [cursor, viewMode]);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get(`/team/schedule/range?agency_id=${agencyId}&from_week=${fromWeek}&to_week=${toWeek}`),
            api.get(`/users`),
        ])
            .then(([sl, op]) => {
                setSlots(sl.data || []);
                setOperators((op.data || []).filter((u) => {
                    const ids = new Set(u.agency_ids || []);
                    if (u.agency_id) ids.add(u.agency_id);
                    return ids.has(agencyId);
                }));
            })
            .finally(() => setLoading(false));
    };

    useEffect(load, [agencyId, fromWeek, toWeek]);

    // Mappa per accesso O(1): slotsByDate["2026-06-22"] = [slot, slot]
    const slotsByDate = useMemo(() => {
        const out = {};
        slots.forEach((s) => {
            const ws = parseISODateLocal(s.week_starting);
            ws.setDate(ws.getDate() + s.weekday);
            const y = ws.getFullYear();
            const m = String(ws.getMonth() + 1).padStart(2, "0");
            const d = String(ws.getDate()).padStart(2, "0");
            const dateKey = `${y}-${m}-${d}`;
            if (!out[dateKey]) out[dateKey] = [];
            out[dateKey].push(s);
        });
        Object.keys(out).forEach((k) => out[k].sort((a, b) => a.start_time.localeCompare(b.start_time)));
        return out;
    }, [slots]);

    // Ore pianificate per operatore nel range corrente
    const hoursByOperator = useMemo(() => {
        const out = {};
        slots.forEach((s) => {
            out[s.operator_name] = (out[s.operator_name] || 0) + diffHours(s.start_time, s.end_time);
        });
        return out;
    }, [slots]);

    const navMonth = (delta) => {
        setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
    };
    const navWeek = (delta) => {
        const d = new Date(cursor);
        d.setDate(d.getDate() + delta * 7);
        setCursor(d);
    };
    const goToday = () => {
        const t = new Date();
        setCursor(viewMode === "month" ? new Date(t.getFullYear(), t.getMonth(), 1) : t);
    };

    const duplicateWeek = async (sourceWeek, targetWeek) => {
        if (!window.confirm(`Duplicare la settimana del ${shortDate(parseISODateLocal(sourceWeek).toISOString())} nella settimana del ${shortDate(parseISODateLocal(targetWeek).toISOString())}? Gli eventuali slot già presenti verranno sovrascritti.`)) return;
        setDuplicating(true);
        try {
            const r = await api.post("/team/schedule/duplicate-week", {
                agency_id: agencyId,
                from_week: sourceWeek,
                to_week: targetWeek,
                overwrite: true,
            });
            toast.success(`${r.data.copied} turni duplicati`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setDuplicating(false);
        }
    };

    const monthLabel = cursor.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    const todayISO = (() => {
        const t = new Date();
        return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    })();

    return (
        <div data-testid="schedule-tab">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => (viewMode === "month" ? navMonth(-1) : navWeek(-1))}
                        className="btn-secondary !py-2 !px-3"
                        data-testid="cal-prev"
                        aria-label="Precedente"
                    >
                        ◀
                    </button>
                    <div className="text-sm font-bold tabular-nums capitalize min-w-[140px] text-center">
                        {viewMode === "month" ? monthLabel : `Sett. ${shortDate(cursor.toISOString())}`}
                    </div>
                    <button
                        onClick={() => (viewMode === "month" ? navMonth(1) : navWeek(1))}
                        className="btn-secondary !py-2 !px-3"
                        data-testid="cal-next"
                        aria-label="Successivo"
                    >
                        ▶
                    </button>
                    <button onClick={goToday} className="text-xs underline text-zinc-500 ml-2" data-testid="cal-today">
                        Oggi
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    {viewMode === "week" && (
                        <button
                            onClick={() => {
                                const cur = startOfWeekISO(cursor);
                                const next = (() => {
                                    const d = parseISODateLocal(cur);
                                    d.setDate(d.getDate() + 7);
                                    return startOfWeekISO(d);
                                })();
                                duplicateWeek(cur, next);
                            }}
                            disabled={duplicating}
                            className="btn-secondary text-xs"
                            data-testid="duplicate-week-next"
                        >
                            {duplicating ? "…" : "Duplica → sett. prossima"}
                        </button>
                    )}
                    <div className="inline-flex border border-zinc-300" role="group">
                        <button
                            onClick={() => setViewMode("month")}
                            className={"px-3 py-1.5 text-xs font-bold uppercase tracking-wider " + (viewMode === "month" ? "bg-ink text-white" : "bg-white text-zinc-700 hover:bg-zinc-50")}
                            data-testid="view-month"
                        >
                            Mese
                        </button>
                        <button
                            onClick={() => setViewMode("week")}
                            className={"px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-l border-zinc-300 " + (viewMode === "week" ? "bg-ink text-white" : "bg-white text-zinc-700 hover:bg-zinc-50")}
                            data-testid="view-week"
                        >
                            Settimana
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="overline">Caricamento…</div>
            ) : viewMode === "month" ? (
                <MonthGrid
                    cursor={cursor}
                    slotsByDate={slotsByDate}
                    todayISO={todayISO}
                    onAdd={(date) => {
                        const wsISO = startOfWeekISO(date);
                        const wd = ((date.getDay() + 6) % 7); // 0=Mon..6=Sun
                        setEditor({ weekday: wd, weekStarting: wsISO, slot: null, date });
                    }}
                    onEdit={(slot) => {
                        const date = parseISODateLocal(slot.week_starting);
                        date.setDate(date.getDate() + slot.weekday);
                        setEditor({ weekday: slot.weekday, weekStarting: slot.week_starting, slot, date });
                    }}
                />
            ) : (
                <WeekGrid
                    weekStart={startOfWeekISO(cursor)}
                    slotsByDate={slotsByDate}
                    todayISO={todayISO}
                    onAdd={(date) => {
                        const wsISO = startOfWeekISO(date);
                        const wd = ((date.getDay() + 6) % 7);
                        setEditor({ weekday: wd, weekStarting: wsISO, slot: null, date });
                    }}
                    onEdit={(slot) => {
                        const date = parseISODateLocal(slot.week_starting);
                        date.setDate(date.getDate() + slot.weekday);
                        setEditor({ weekday: slot.weekday, weekStarting: slot.week_starting, slot, date });
                    }}
                />
            )}

            {/* Footer: ore pianificate per operatore */}
            {Object.keys(hoursByOperator).length > 0 && (
                <div className="mt-6 bg-white border border-zinc-200 p-4">
                    <div className="overline mb-3">Ore pianificate · {viewMode === "month" ? "mese" : "settimana"}</div>
                    <div className="flex flex-wrap gap-3">
                        {Object.entries(hoursByOperator)
                            .sort(([, a], [, b]) => b - a)
                            .map(([name, hrs]) => {
                                const c = operatorColor(name);
                                return (
                                    <div
                                        key={name}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 border"
                                        style={{ background: c.bg, color: c.fg, borderColor: c.bar }}
                                    >
                                        <span className="font-bold text-sm">{name}</span>
                                        <span className="font-black tabular-nums text-sm">{hFmt(hrs)}</span>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}

            {editor && (
                <SlotEditor
                    weekday={editor.weekday}
                    slot={editor.slot}
                    operators={operators}
                    agencyId={agencyId}
                    weekStarting={editor.weekStarting}
                    date={editor.date}
                    onClose={() => setEditor(null)}
                    onSaved={() => { setEditor(null); load(); }}
                />
            )}
        </div>
    );
}

function MonthGrid({ cursor, slotsByDate, todayISO, onAdd, onEdit }) {
    // Costruisce 6 righe (max 42 giorni) coprendo tutto il mese
    const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = parseISODateLocal(startOfWeekISO(firstOfMonth));
    const cells = [];
    for (let i = 0; i < 42; i++) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        cells.push(d);
    }
    // Trimma a 5 righe se la 6° non serve (= primo giorno della riga è nel mese successivo)
    const lastRowStart = cells[35];
    const trimmed = lastRowStart.getMonth() !== cursor.getMonth() ? cells.slice(0, 35) : cells;

    return (
        <div className="bg-white border border-zinc-200 overflow-hidden" data-testid="month-grid">
            {/* header weekdays */}
            <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
                {WEEKDAYS.map((w) => (
                    <div key={w} className="overline text-center py-2">{w}</div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {trimmed.map((d, idx) => {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    const day = String(d.getDate()).padStart(2, "0");
                    const dateKey = `${y}-${m}-${day}`;
                    const inMonth = d.getMonth() === cursor.getMonth();
                    const isToday = dateKey === todayISO;
                    const daySlots = slotsByDate[dateKey] || [];
                    const visible = daySlots.slice(0, 3);
                    const extra = daySlots.length - visible.length;
                    return (
                        <div
                            key={idx}
                            className={
                                "border-b border-r border-zinc-100 min-h-[100px] p-1.5 relative group " +
                                (inMonth ? "" : "bg-zinc-50/50 text-zinc-300")
                            }
                            data-testid={`day-${dateKey}`}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <div
                                    className={
                                        "text-xs font-bold tabular-nums " +
                                        (isToday ? "bg-ink text-white px-1.5 py-0.5" : "")
                                    }
                                >
                                    {d.getDate()}
                                </div>
                                <button
                                    onClick={() => onAdd(d)}
                                    className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-ink transition-opacity"
                                    aria-label="Aggiungi turno"
                                    data-testid={`add-day-${dateKey}`}
                                >
                                    <Plus size={12} weight="bold" />
                                </button>
                            </div>
                            <div className="space-y-0.5">
                                {visible.map((s) => {
                                    const c = operatorColor(s.operator_name);
                                    return (
                                        <button
                                            key={s.id}
                                            onClick={() => onEdit(s)}
                                            className="block w-full text-left px-1.5 py-0.5 border-l-2 truncate text-[10px] leading-tight hover:opacity-80"
                                            style={{ background: c.bg, color: c.fg, borderColor: c.bar }}
                                            data-testid={`slot-${s.id}`}
                                            title={`${s.operator_name} · ${s.start_time}–${s.end_time}`}
                                        >
                                            <span className="font-bold tabular-nums">{s.start_time}</span>{" "}
                                            <span className="opacity-80">{s.operator_name.split(" ")[0]}</span>
                                        </button>
                                    );
                                })}
                                {extra > 0 && (
                                    <button
                                        onClick={() => onEdit(daySlots[0])}
                                        className="text-[10px] font-bold text-zinc-500 hover:text-ink px-1"
                                    >
                                        +{extra} altri
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function WeekGrid({ weekStart, slotsByDate, todayISO, onAdd, onEdit }) {
    const wsDate = parseISODateLocal(weekStart);
    const days = WEEKDAYS.map((label, idx) => {
        const d = new Date(wsDate);
        d.setDate(d.getDate() + idx);
        return { idx, label, date: d };
    });
    return (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="schedule-grid">
            {days.map((d) => {
                const y = d.date.getFullYear();
                const m = String(d.date.getMonth() + 1).padStart(2, "0");
                const day = String(d.date.getDate()).padStart(2, "0");
                const dateKey = `${y}-${m}-${day}`;
                const daySlots = slotsByDate[dateKey] || [];
                const isToday = dateKey === todayISO;
                return (
                    <div key={d.idx} className={"bg-white border min-h-[200px] flex flex-col " + (isToday ? "border-ink" : "border-zinc-200")}>
                        <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                            <div>
                                <div className="overline">{d.label}</div>
                                <div className={"text-xs font-bold tabular-nums " + (isToday ? "text-ink" : "")}>{d.date.getDate()}/{d.date.getMonth() + 1}</div>
                            </div>
                            <button
                                onClick={() => onAdd(d.date)}
                                className="text-zinc-400 hover:text-ink"
                                aria-label="Aggiungi slot"
                                data-testid={`add-slot-${d.idx}`}
                            >
                                <Plus size={14} weight="bold" />
                            </button>
                        </div>
                        <div className="flex-1 p-2 space-y-1">
                            {daySlots.length === 0 && (
                                <div className="text-xs text-zinc-300 text-center mt-3">—</div>
                            )}
                            {daySlots.map((s) => {
                                const c = operatorColor(s.operator_name);
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => onEdit(s)}
                                        className="w-full px-2 py-1.5 hover:opacity-80 border-l-2 text-left"
                                        style={{ background: c.bg, color: c.fg, borderColor: c.bar }}
                                        data-testid={`slot-${s.id}`}
                                    >
                                        <div className="text-xs font-bold truncate">{s.operator_name}</div>
                                        <div className="overline tabular-nums">{s.start_time} – {s.end_time}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


function SlotEditor({ weekday, slot, operators, agencyId, weekStarting, date, onClose, onSaved }) {
    const [operatorId, setOperatorId] = useState(slot?.operator_id || (operators[0]?.id || ""));
    const [startTime, setStartTime] = useState(slot?.start_time || "09:00");
    const [endTime, setEndTime] = useState(slot?.end_time || "14:00");
    const [notes, setNotes] = useState(slot?.notes || "");
    const [saving, setSaving] = useState(false);
    const [presets, setPresets] = useState([]);

    // Carica i preset turni configurati per l'agenzia (fallback ai default se vuoto)
    useEffect(() => {
        api.get(`/agencies/${agencyId}/shift-presets`)
            .then((r) => setPresets(r.data.presets || []))
            .catch(() => setPresets([]));
    }, [agencyId]);

    const dateLabel = date
        ? date.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })
        : WEEKDAYS[weekday];

    const save = async () => {
        if (!operatorId) {
            toast.error("Seleziona un operatore");
            return;
        }
        setSaving(true);
        try {
            if (slot) {
                await api.patch(`/team/schedule/${slot.id}`, {
                    operator_id: operatorId, start_time: startTime, end_time: endTime, notes,
                });
            } else {
                await api.post(`/team/schedule`, {
                    agency_id: agencyId, operator_id: operatorId, weekday,
                    start_time: startTime, end_time: endTime, week_starting: weekStarting, notes,
                });
            }
            toast.success(slot ? "Slot aggiornato" : "Slot creato");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const duplicateToNextDay = async () => {
        if (!slot) return;
        setSaving(true);
        try {
            // Calcola la prossima posizione (weekday+1, eventuale rollover di settimana)
            let nextWeekday = weekday + 1;
            let nextWeekStarting = weekStarting;
            if (nextWeekday > 6) {
                nextWeekday = 0;
                const d = parseISODateLocal(weekStarting);
                d.setDate(d.getDate() + 7);
                nextWeekStarting = startOfWeekISO(d);
            }
            await api.post(`/team/schedule`, {
                agency_id: agencyId,
                operator_id: slot.operator_id,
                weekday: nextWeekday,
                start_time: slot.start_time,
                end_time: slot.end_time,
                week_starting: nextWeekStarting,
                notes: slot.notes || "",
            });
            toast.success("Duplicato al giorno successivo");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!slot || !window.confirm("Eliminare lo slot?")) return;
        setSaving(true);
        try {
            await api.delete(`/team/schedule/${slot.id}`);
            toast.success("Slot eliminato");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-white/80 backdrop-blur-sm z-[60] overflow-y-auto"
            onClick={onClose}
            data-testid="slot-editor"
        >
            <div className="flex min-h-full items-center justify-center p-4">
                <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white border border-zinc-200 p-6 md:p-8 shadow-2xl">
                    <div className="overline mb-3 capitalize">Schedulazione · {dateLabel}</div>
                    <h2 className="text-2xl font-black tracking-tighter mb-6">
                        {slot ? "Modifica turno" : "Nuovo turno"}
                    </h2>
                    <label className="block mb-4">
                        <div className="overline mb-1">Operatore</div>
                        <select className="inp" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} data-testid="slot-operator">
                            {operators.map((o) => (
                                <option key={o.id} value={o.id}>{o.name} ({o.role})</option>
                            ))}
                        </select>
                    </label>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <label className="block">
                            <div className="overline mb-1">Inizio</div>
                            <input type="time" className="inp" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="slot-start" />
                        </label>
                        <label className="block">
                            <div className="overline mb-1">Fine</div>
                            <input type="time" className="inp" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="slot-end" />
                        </label>
                    </div>
                    {/* Slot rapidi: facilita la pianificazione comune.
                        Configurabili dall'owner in Impostazioni → Agenzia → Preset turni */}
                    {presets.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-1.5">
                            {presets.map((p) => (
                                <button
                                    key={`${p.label}-${p.start_time}`}
                                    type="button"
                                    onClick={() => { setStartTime(p.start_time); setEndTime(p.end_time); }}
                                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-zinc-200 hover:border-ink"
                                    data-testid={`preset-pill-${p.label}`}
                                >
                                    {p.label} · {p.start_time}–{p.end_time}
                                </button>
                            ))}
                        </div>
                    )}
                    <label className="block mb-4">
                        <div className="overline mb-1">Note</div>
                        <input className="inp" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="opzionale" data-testid="slot-notes" />
                    </label>
                    {slot && (
                        <button
                            type="button"
                            onClick={duplicateToNextDay}
                            disabled={saving}
                            className="w-full text-xs underline text-zinc-500 hover:text-ink mb-4"
                            data-testid="slot-duplicate-next"
                        >
                            Duplica al giorno successivo →
                        </button>
                    )}
                    <div className="flex gap-2">
                        {slot && (
                            <button onClick={remove} disabled={saving} className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="slot-delete">
                                <Trash size={12} weight="bold" />
                            </button>
                        )}
                        <button onClick={onClose} className="btn-secondary flex-1" data-testid="slot-cancel">Annulla</button>
                        <button onClick={save} disabled={saving} className="btn-primary flex-1" data-testid="slot-save">
                            {saving ? "…" : "Salva"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
